import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  Dimensions,
  Platform
} from 'react-native';
import {
  XStack,
  YStack,
  Button,
  Card,
  Separator,
  H4,
  H6,
  Sheet,
  Spinner
} from 'tamagui';
import {
  MaterialIcons,
  AntDesign,
  FontAwesome5,
  Ionicons
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

const AIProjectCreationModal = forwardRef((props, ref) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [projectData, setProjectData] = useState({
    projectDescription: '',
    industry: 'general',
    projectType: 'standard',
    timeline: 'medium',
    teamSize: 'small',
    budget: 'medium',
    requirements: [],
    features: []
  });
  const [newRequirement, setNewRequirement] = useState('');
  const [newFeature, setNewFeature] = useState('');

  const theme = themeSelector();
  const queryClient = useQueryClient();

  // Get AI project templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['aiProjectTemplates'],
    queryFn: apis.getAIProjectTemplates,
    enabled: isVisible
  });

  // AI Project Creation Mutation
  const createAIProjectMutation = useMutation({
    mutationFn: apis.createAIProject,
    onSuccess: (response) => {
      Alert.alert(
        'Success!',
        'Your AI-generated project has been created successfully!',
        [
          {
            text: 'OK',
            onPress: () => {
              closeModal();
              props.callBack?.();
            }
          }
        ]
      );
    },
    onError: (error) => {
      console.error('AI Project Creation Error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create AI project. Please try again.';
      Alert.alert('Error', errorMessage);
    }
  });

  useImperativeHandle(ref, () => ({
    openModal: () => setIsVisible(true),
    closeModal: () => closeModal()
  }));

  const closeModal = () => {
    setIsVisible(false);
    setCurrentStep(1);
    setProjectData({
      projectDescription: '',
      industry: 'general',
      projectType: 'standard',
      timeline: 'medium',
      teamSize: 'small',
      budget: 'medium',
      requirements: [],
      features: []
    });
    setNewRequirement('');
    setNewFeature('');
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setProjectData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setProjectData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setProjectData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setProjectData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleNextStep = () => {
    if (currentStep === 1 && projectData.projectDescription.length < 10) {
      Alert.alert('Required', 'Please provide a project description of at least 10 characters.');
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleCreateProject = () => {
    if (!projectData.projectDescription.trim()) {
      Alert.alert('Required', 'Project description is required.');
      return;
    }

    createAIProjectMutation.mutate(projectData);
  };

  const renderStepIndicator = () => (
    <XStack gap={16} justifyContent="center" alignItems="center" marginBottom={20}>
      {[1, 2, 3].map((step) => (
        <XStack key={step} alignItems="center">
          <View
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              backgroundColor: currentStep >= step ? '#007AFF' : theme ? '#E5E7EB' : '#374151',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Text style={{
              color: currentStep >= step ? 'white' : theme ? '#6B7280' : '#9CA3AF',
              fontWeight: 'bold'
            }}>
              {step}
            </Text>
          </View>
          {step < 3 && (
            <View
              style={{
                width: 40,
                height: 2,
                backgroundColor: currentStep > step ? '#007AFF' : theme ? '#E5E7EB' : '#374151',
                marginHorizontal: 8
              }}
            />
          )}
        </XStack>
      ))}
    </XStack>
  );

  const renderStep1 = () => (
    <YStack gap={20}>
      <YStack gap={12}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
          Describe Your Project
        </Text>
        <TextInput
          style={{
            backgroundColor: theme ? '#F9FAFB' : '#1F2937',
            borderColor: theme ? '#D1D5DB' : '#374151',
            borderWidth: 1,
            borderRadius: 8,
            padding: 12,
            color: theme ? '#000' : '#fff',
            fontSize: 14,
            minHeight: 100,
            textAlignVertical: 'top'
          }}
          placeholder="Describe what you want to build... (e.g., A mobile app for task management with team collaboration features)"
          placeholderTextColor={theme ? '#6B7280' : '#9CA3AF'}
          value={projectData.projectDescription}
          onChangeText={(text) => setProjectData(prev => ({ ...prev, projectDescription: text }))}
          multiline
          numberOfLines={4}
        />
      </YStack>

      <YStack gap={12}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
          Industry
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <XStack gap={12}>
            {templates?.data?.industries?.map((industry) => (
              <TouchableOpacity
                key={industry}
                onPress={() => setProjectData(prev => ({ ...prev, industry }))}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 20,
                  backgroundColor: projectData.industry === industry ? '#007AFF' : theme ? '#F3F4F6' : '#374151',
                  borderWidth: 1,
                  borderColor: projectData.industry === industry ? '#007AFF' : theme ? '#D1D5DB' : '#4B5563'
                }}
              >
                <Text style={{
                  color: projectData.industry === industry ? 'white' : theme ? '#374151' : '#D1D5DB',
                  fontSize: 14,
                  fontWeight: projectData.industry === industry ? 'bold' : 'normal'
                }}>
                  {industry.replace('_', ' ').toUpperCase()}
                </Text>
              </TouchableOpacity>
            ))}
          </XStack>
        </ScrollView>
      </YStack>
    </YStack>
  );

  const renderStep2 = () => (
    <YStack gap={20}>
      <YStack gap={12}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
          Project Type
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <XStack gap={12}>
            {templates?.data?.project_types?.map((type) => (
              <TouchableOpacity
                key={type}
                onPress={() => setProjectData(prev => ({ ...prev, projectType: type }))}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 20,
                  backgroundColor: projectData.projectType === type ? '#007AFF' : theme ? '#F3F4F6' : '#374151',
                  borderWidth: 1,
                  borderColor: projectData.projectType === type ? '#007AFF' : theme ? '#D1D5DB' : '#4B5563'
                }}
              >
                <Text style={{
                  color: projectData.projectType === type ? 'white' : theme ? '#374151' : '#D1D5DB',
                  fontSize: 14,
                  fontWeight: projectData.projectType === type ? 'bold' : 'normal'
                }}>
                  {type.replace('_', ' ').toUpperCase()}
                </Text>
              </TouchableOpacity>
            ))}
          </XStack>
        </ScrollView>
      </YStack>

      <XStack gap={12} flexWrap="wrap">
        <YStack flex={1} gap={8}>
          <Text style={{ fontSize: 14, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
            Timeline
          </Text>
          {templates?.data?.timeline_options?.map((option) => (
            <TouchableOpacity
              key={option}
              onPress={() => setProjectData(prev => ({ ...prev, timeline: option }))}
              style={{
                padding: 12,
                borderRadius: 8,
                backgroundColor: projectData.timeline === option ? '#007AFF' : theme ? '#F3F4F6' : '#374151',
                borderWidth: 1,
                borderColor: projectData.timeline === option ? '#007AFF' : theme ? '#D1D5DB' : '#4B5563'
              }}
            >
              <Text style={{
                color: projectData.timeline === option ? 'white' : theme ? '#374151' : '#D1D5DB',
                fontSize: 12,
                fontWeight: projectData.timeline === option ? 'bold' : 'normal',
                textAlign: 'center'
              }}>
                {option.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ))}
        </YStack>

        <YStack flex={1} gap={8}>
          <Text style={{ fontSize: 14, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
            Team Size
          </Text>
          {templates?.data?.team_sizes?.map((size) => (
            <TouchableOpacity
              key={size}
              onPress={() => setProjectData(prev => ({ ...prev, teamSize: size }))}
              style={{
                padding: 12,
                borderRadius: 8,
                backgroundColor: projectData.teamSize === size ? '#007AFF' : theme ? '#F3F4F6' : '#374151',
                borderWidth: 1,
                borderColor: projectData.teamSize === size ? '#007AFF' : theme ? '#D1D5DB' : '#4B5563'
              }}
            >
              <Text style={{
                color: projectData.teamSize === size ? 'white' : theme ? '#374151' : '#D1D5DB',
                fontSize: 12,
                fontWeight: projectData.teamSize === size ? 'bold' : 'normal',
                textAlign: 'center'
              }}>
                {size.toUpperCase()}
              </Text>
            </TouchableOpacity>
          ))}
        </YStack>
      </XStack>
    </YStack>
  );

  const renderStep3 = () => (
    <YStack gap={20}>
      <YStack gap={12}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
          Requirements (Optional)
        </Text>
        <XStack gap={8}>
          <TextInput
            style={{
              flex: 1,
              backgroundColor: theme ? '#F9FAFB' : '#1F2937',
              borderColor: theme ? '#D1D5DB' : '#374151',
              borderWidth: 1,
              borderRadius: 8,
              padding: 12,
              color: theme ? '#000' : '#fff',
              fontSize: 14
            }}
            placeholder="Add a requirement..."
            placeholderTextColor={theme ? '#6B7280' : '#9CA3AF'}
            value={newRequirement}
            onChangeText={setNewRequirement}
            onSubmitEditing={addRequirement}
          />
          <TouchableOpacity
            onPress={addRequirement}
            style={{
              backgroundColor: '#007AFF',
              borderRadius: 8,
              padding: 12,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AntDesign name="plus" size={16} color="white" />
          </TouchableOpacity>
        </XStack>
        <ScrollView style={{ maxHeight: 100 }}>
          {projectData.requirements.map((req, index) => (
            <XStack key={index} justifyContent="space-between" alignItems="center" marginBottom={8}>
              <Text style={{
                color: theme ? '#374151' : '#D1D5DB',
                fontSize: 14,
                flex: 1
              }}>
                • {req}
              </Text>
              <TouchableOpacity onPress={() => removeRequirement(index)}>
                <AntDesign name="close" size={16} color="#EF4444" />
              </TouchableOpacity>
            </XStack>
          ))}
        </ScrollView>
      </YStack>

      <YStack gap={12}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
          Key Features (Optional)
        </Text>
        <XStack gap={8}>
          <TextInput
            style={{
              flex: 1,
              backgroundColor: theme ? '#F9FAFB' : '#1F2937',
              borderColor: theme ? '#D1D5DB' : '#374151',
              borderWidth: 1,
              borderRadius: 8,
              padding: 12,
              color: theme ? '#000' : '#fff',
              fontSize: 14
            }}
            placeholder="Add a feature..."
            placeholderTextColor={theme ? '#6B7280' : '#9CA3AF'}
            value={newFeature}
            onChangeText={setNewFeature}
            onSubmitEditing={addFeature}
          />
          <TouchableOpacity
            onPress={addFeature}
            style={{
              backgroundColor: '#007AFF',
              borderRadius: 8,
              padding: 12,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AntDesign name="plus" size={16} color="white" />
          </TouchableOpacity>
        </XStack>
        <ScrollView style={{ maxHeight: 100 }}>
          {projectData.features.map((feature, index) => (
            <XStack key={index} justifyContent="space-between" alignItems="center" marginBottom={8}>
              <Text style={{
                color: theme ? '#374151' : '#D1D5DB',
                fontSize: 14,
                flex: 1
              }}>
                • {feature}
              </Text>
              <TouchableOpacity onPress={() => removeFeature(index)}>
                <AntDesign name="close" size={16} color="#EF4444" />
              </TouchableOpacity>
            </XStack>
          ))}
        </ScrollView>
      </YStack>

      <YStack gap={12}>
        <Text style={{ fontSize: 16, fontWeight: 'bold', color: theme ? '#000' : '#fff' }}>
          Budget Range
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <XStack gap={12}>
            {templates?.data?.budget_ranges?.map((budget) => (
              <TouchableOpacity
                key={budget}
                onPress={() => setProjectData(prev => ({ ...prev, budget }))}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 20,
                  backgroundColor: projectData.budget === budget ? '#007AFF' : theme ? '#F3F4F6' : '#374151',
                  borderWidth: 1,
                  borderColor: projectData.budget === budget ? '#007AFF' : theme ? '#D1D5DB' : '#4B5563'
                }}
              >
                <Text style={{
                  color: projectData.budget === budget ? 'white' : theme ? '#374151' : '#D1D5DB',
                  fontSize: 14,
                  fontWeight: projectData.budget === budget ? 'bold' : 'normal'
                }}>
                  {budget.toUpperCase()}
                </Text>
              </TouchableOpacity>
            ))}
          </XStack>
        </ScrollView>
      </YStack>
    </YStack>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return null;
    }
  };

  if (Platform.OS === 'web') {
    return (
      <Sheet
        modal
        open={isVisible}
        onOpenChange={setIsVisible}
        snapPoints={[85]}
        dismissOnSnapToBottom
      >
        <Sheet.Overlay />
        <Sheet.Handle />
        <Sheet.Frame
          padding={20}
          backgroundColor={theme ? '#FFFFFF' : '#1F2937'}
          borderTopLeftRadius={20}
          borderTopRightRadius={20}
        >
          <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
            <YStack gap={24}>
              <XStack justifyContent="space-between" alignItems="center">
                <H4 color={theme ? '#000' : '#fff'}>
                  Create AI Project
                </H4>
                <TouchableOpacity onPress={closeModal}>
                  <AntDesign name="close" size={24} color={theme ? '#000' : '#fff'} />
                </TouchableOpacity>
              </XStack>

              {renderStepIndicator()}

              {templatesLoading ? (
                <YStack alignItems="center" justifyContent="center" padding={40}>
                  <Spinner size="large" color="#007AFF" />
                  <Text style={{ color: theme ? '#000' : '#fff', marginTop: 16 }}>
                    Loading templates...
                  </Text>
                </YStack>
              ) : (
                renderStepContent()
              )}

              <XStack justifyContent="space-between" marginTop={20}>
                <Button
                  onPress={handlePrevStep}
                  disabled={currentStep === 1}
                  variant={currentStep === 1 ? "outlined" : "default"}
                  backgroundColor={currentStep === 1 ? 'transparent' : theme ? '#F3F4F6' : '#374151'}
                  color={theme ? '#374151' : '#D1D5DB'}
                  borderColor={theme ? '#D1D5DB' : '#4B5563'}
                >
                  Previous
                </Button>

                {currentStep < 3 ? (
                  <Button
                    onPress={handleNextStep}
                    backgroundColor="#007AFF"
                    color="white"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    onPress={handleCreateProject}
                    disabled={createAIProjectMutation.isPending}
                    backgroundColor="#007AFF"
                    color="white"
                  >
                    {createAIProjectMutation.isPending ? (
                      <XStack alignItems="center" gap={8}>
                        <Spinner size="small" color="white" />
                        <Text style={{ color: 'white' }}>Creating...</Text>
                      </XStack>
                    ) : (
                      'Create AI Project'
                    )}
                  </Button>
                )}
              </XStack>
            </YStack>
          </ScrollView>
        </Sheet.Frame>
      </Sheet>
    );
  }

  // Mobile/Native version would use a different modal implementation
  return null;
});

export default AIProjectCreationModal;